import MaterialTable from '@material-table/core';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert, Snackbar,
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Table,
  TableBody, TableCell, TableHead, TableRow, Tooltip
} from '@mui/material';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import { confirmDialog } from 'common/ComfirmationDialog';
import * as Constant from 'common/Constant';
import { RENEWAL_ACCOUNT_TYPE_MAPPING } from 'common/Constant';
import materialTableIcons from 'common/materialTableIcons';
import { getAllBrokers } from 'features/user/userActions';
import * as UserTypes from 'features/user/userTypes';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { store } from 'store/store';
import { fetchBrokerCompanyMapping } from '../vendor-labeling/actions/action';
import * as Action from './action/action';
import TenantCreationDialog from './components/tenantCreationDialog';

export const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const INACTIVE_TENANT_MESSAGE =
  'Deactivating this tenant will disable access and permanently remove AI summary data to meet data deletion requirements.<br /><br />AI Summary and Go-Live will be automatically deselected due to the tenant being inactive.';

export const FILTER_OPTION_SETTINGS = [
  { title: 'Date Basis Filter', field: 'dateBasisEnabled' },
  { title: 'Date Range Filter', field: 'dateRangeEnabled' },
  { title: 'Rolling 12 Filter', field: 'rolling12Enabled' },
  { title: 'Plan YTD Vs Prior Plan YTD Filter', field: 'planYTDVsPriorPlanYTDEnabled' },
  { title: 'Plan YTD Vs Prior Plan Year Filter', field: 'planYTDVsPriorPlanYearEnabled' },
  { title: 'Custom Range Filter', field: 'customRangeEnabled' },
  { title: 'Divisions Filter', field: 'divisionsEnabled' },
  { title: 'Plans Filter', field: 'plansEnabled' },
  { title: 'Cohort Builder Filter', field: 'cohortBuilderEnabled' },
  { title: 'Use Cohort Builder PHI', field: 'phiCohortBuilderEnabled' },
];

const TenantManagement = () => {
  const tableRef = useRef({});

  const shouldRefresh = useSelector((state) => state.tenantManagement.shouldRefresh);
  // Retrieve user email from Redux state
  const userEmail = useSelector((state) => state.user.email);

  const [creationOpen, setCreationOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const tenantData = useRef({});

  const handleCreationOpen = (rowData) => {
    tenantData.current = rowData;
    setCreationOpen(true);
  };

  const handleCreationClose = () => {
    setCreationOpen(false);
    tenantData.current = null;
  };

  const [pageSize, setPageSize] = useState(Constant.DEFAULT_SELECT_PAGE_SIZE);

  const refresh = () => {
    tableRef.current && tableRef.current.onQueryChange();
  };

  useEffect(() => {
    // Send tenant management action to Clarity
    if (window.clarity && userEmail) {
      window.clarity('set', 'tenantManagementAction', userEmail.toLowerCase());
    }
  }, [userEmail]);

  const handleTenantActiveCheckboxChange = (payload) => {
    if (payload.active) {
      confirmDialog(
        () => store.dispatch(Action.activateCompany(payload.id)),
        `Are you sure you want to change the status of "${escapeHtml(payload.name)}" to active?`
      );
      return;
    }

    confirmDialog(
      () => store.dispatch(Action.deactivateTenant(payload)),
      INACTIVE_TENANT_MESSAGE,
      'Deactivate Tenant?',
      'Cancel',
      'Deactivate Tenant'
    );
  };

  const handleTenantOnboardedCheckboxChange = (payload) => {
    payload.state = payload.stateId;
    const { brokerCode, brokerName, stateId, tableData, ...rest } = payload;
    confirmDialog(
      () => store.dispatch(Action.syncTenantGoLiveAndAiSummary(rest)),
      `Are you sure you want to change the status of "${escapeHtml(payload.name)}" to ${payload.live ? 'live' : 'not live'}?`
    );
  };

  const handleAiSummaryCheckboxChange = (payload) => {
    confirmDialog(
      () => store.dispatch(Action.toggleAiSummary(payload.id, payload.aiSummaryEnabled)),
      `Are you sure you want to ${payload.aiSummaryEnabled ? 'enable' : 'disable'} AI Summary for "${escapeHtml(payload.name)}"?`
    );
  };

  const handleCampaignEnabledCheckboxChange = (payload) => {
    confirmDialog(
      () => store.dispatch(Action.toggleCampaignEnabled(payload.id, payload.campaignEnabled)),
      `Are you sure you want to ${payload.campaignEnabled ? 'enable' : 'disable'} Workday Integration for "${escapeHtml(payload.name)}"?`
    );
  };

  const handleTenantFiltersCheckboxChange = (payload) => {
    confirmDialog(
      () => store.dispatch(Action.updateTenantFiltersEnabled(payload.id, payload.filtersEnabled)),
      `Are you sure you want to ${payload.filtersEnabled ? 'enable' : 'disable'} the Filters for "${payload.name}"?`
    );
  };

  const handleFilterOptionCheckboxChange = (rowData, field, enabled, title) => {
    const updatedTenant = {
      ...rowData,
      [field]: enabled,
      state: rowData.stateId
    };
    const { brokerCode, brokerName, stateId, tableData, ...payload } = updatedTenant;

    confirmDialog(
      async () => {
        try {
          await store.dispatch(Action.updateTenantDetails(payload));
        } catch (err) {
          setErrorMessage(err.message || 'Error');
        }
      },
      `Are you sure you want to ${enabled ? 'enable' : 'disable'} the ${title} for "${escapeHtml(rowData.name)}"?`
    );
  };

  useEffect(() => {
    refresh();
  }, [shouldRefresh]);

  useEffect(() => {
    store.dispatch({
      type: UserTypes.APP_SWITCHED,
      payload: AppConfig.HEADER_MENU_TYPE.NONE
    });
    store.dispatch(getAllBrokers());
    store.dispatch(fetchBrokerCompanyMapping());
    store.dispatch(Action.fetchStateList());
  }, []);

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditTarget, setAuditTarget] = useState(null);

  const handleAuditOpen = async (rowData) => {
    setAuditTarget(rowData);
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const res = await axios.get(`${AppConfig.FO_INITIAL_URL}/company-audit/changes/${rowData.code}`);
      setAuditRows(res.data || []);
    } catch (e) {
      setAuditRows([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleAuditClose = () => {
    setAuditOpen(false);
    setAuditRows([]);
    setAuditTarget(null);
  };

  return (
    <>
      <MaterialTable
        title=""
        style={{ width: '100%' }}
        tableRef={tableRef}
        columns={[
          { title: 'Code', field: 'code', editable: 'never' },
          { title: 'Name', field: 'name', editable: 'never' },
          { title: 'Broker', field: 'brokerName', editable: 'never' },
          { title: 'State', field: 'state', editable: 'never' },
          {
            title: 'Renewal Account Type',
            field: 'renewalAccountType',
            editable: 'never',
            render: (data) => RENEWAL_ACCOUNT_TYPE_MAPPING[data.renewalAccountType] || 'Unknown'
          },
          {
            title: 'Active',
            field: 'active',
            type: 'boolean',
            cellStyle: { textAlign: 'start' },
            headerStyle: { textAlign: 'center' },
            render: (rowdata) => (
              <Checkbox
                checked={rowdata.active}
                onChange={(_, checked) =>
                  handleTenantActiveCheckboxChange({
                    ...rowdata,
                    active: checked
                  })
                }
                inputProps={{ 'aria-label': 'company active checkbox' }}
                id={`input-activeCheckbox-company-active${rowdata.id}`}
                color="primary"
              />
            )
          },
          {
            title: 'AI Summary',
            field: 'aiSummaryEnabled',
            type: 'boolean',
            cellStyle: { textAlign: 'start' },
            headerStyle: { textAlign: 'center' },
            render: (rowdata) =>
              !rowdata?.active ? (
                <Tooltip title="Active status is required">
                  <span>
                    <Checkbox
                      checked={rowdata.aiSummaryEnabled}
                      onChange={(_, checked) =>
                        handleAiSummaryCheckboxChange({
                          ...rowdata,
                          aiSummaryEnabled: checked
                        })
                      }
                      inputProps={{ 'aria-label': 'ai summary checkbox' }}
                      id={`input-aiSummaryCheckbox-company-${rowdata.id}`}
                      color="primary"
                      disabled={!rowdata?.active}
                    />
                  </span>
                </Tooltip>
              ) : (
                <Checkbox
                  checked={rowdata.aiSummaryEnabled}
                  onChange={(_, checked) =>
                    handleAiSummaryCheckboxChange({
                      ...rowdata,
                      aiSummaryEnabled: checked
                    })
                  }
                  inputProps={{ 'aria-label': 'ai summary checkbox' }}
                  id={`input-aiSummaryCheckbox-company-${rowdata.id}`}
                  color="primary"
                  disabled={!rowdata?.active}
                />
              )
          },
          {
            title: 'Workday Integration',
            field: 'campaignEnabled',
            type: 'boolean',
            cellStyle: { textAlign: 'start' },
            headerStyle: { textAlign: 'center' },
            render: (rowdata) =>
              !rowdata?.active ? (
                <Tooltip title="Active status is required">
                  <span>
                    <Checkbox
                      checked={rowdata.campaignEnabled}
                      onChange={(_, checked) =>
                        handleCampaignEnabledCheckboxChange({
                          ...rowdata,
                          campaignEnabled: checked
                        })
                      }
                      inputProps={{ 'aria-label': 'workday integration checkbox' }}
                      id={`input-campaignEnabledCheckbox-company-${rowdata.id}`}
                      color="primary"
                      disabled={!rowdata?.active}
                    />
                  </span>
                </Tooltip>
              ) : (
                <Checkbox
                  checked={rowdata.campaignEnabled}
                  onChange={(_, checked) =>
                    handleCampaignEnabledCheckboxChange({
                      ...rowdata,
                      campaignEnabled: checked
                    })
                  }
                  inputProps={{ 'aria-label': 'workday integration checkbox' }}
                  id={`input-campaignEnabledCheckbox-company-${rowdata.id}`}
                  color="primary"
                  disabled={!rowdata?.active}
                />
              )
          },
          {
            title: 'Go-Live',
            field: 'live',
            type: 'boolean',
            cellStyle: { textAlign: 'start' },
            headerStyle: { textAlign: 'center' },
            render: (rowdata) =>
              !rowdata?.active ? (
                <Tooltip title="Active status is required">
                  <span>
                    <Checkbox
                      checked={rowdata.live}
                      onChange={(_, checked) =>
                        handleTenantOnboardedCheckboxChange({
                          ...rowdata,
                          live: checked
                        })
                      }
                      inputProps={{ 'aria-label': 'company onboarded checkbox' }}
                      id={`input-onboarded-Checkbox-company-onboarded${rowdata.id}`}
                      color="primary"
                      disabled={!rowdata?.active}
                    />
                  </span>
                </Tooltip>
              ) : (
                <Checkbox
                  checked={rowdata.live}
                  onChange={(_, checked) =>
                    handleTenantOnboardedCheckboxChange({
                      ...rowdata,
                      live: checked
                    })
                  }
                  inputProps={{ 'aria-label': 'company onboarded checkbox' }}
                  id={`input-onboarded-Checkbox-company-onboarded${rowdata.id}`}
                  color="primary"
                  disabled={!rowdata?.active}
                />
              )
          },
          {
            title: 'Filters',
            field: 'filtersEnabled',
            type: 'boolean',
            cellStyle: { textAlign: 'start' },
            headerStyle: { textAlign: 'center' },
            render: (rowdata) => (
              <Checkbox
                checked={rowdata.filtersEnabled !== false}
                onChange={(_, checked) =>
                  handleTenantFiltersCheckboxChange({
                    ...rowdata,
                    filtersEnabled: checked
                  })
                }
                inputProps={{ 'aria-label': 'company filters enabled checkbox' }}
                id={`input-filters-enabled-company-${rowdata.id}`}
                color="primary"
              />
            )
          }
        ]}
        data={(query) =>
          new Promise((resolve, reject) => {
            axios
              .post(AppConfig.FO_INITIAL_URL + `/company-admin/search`, {
                page: query.page,
                size: query.pageSize,
                direction: query.orderDirection ? query.orderDirection.toUpperCase() : Constant.DIRECTION_ASC,
                sortProperty: query.orderBy ? query.orderBy.field : 'code',
                searchTerm: query.search
              })
              .then((m) => {
                let data = m.data;
                resolve({
                  data: data.content,
                  page: data.number,
                  size: data.size,
                  totalCount: data.totalElements
                });
              })
              .catch((err) => {
                resolve({
                  data: [],
                  page: 0,
                  totalCount: 0
                });
              });
          })
        }
        onChangeRowsPerPage={setPageSize}
        options={{
          actionsColumnIndex: -1,
          search: true,
          pageSizeOptions: Constant.DEFAULT_SEARCH_PAGE_SIZE,
          pageSize,
          debounceInterval: 400
        }}
        components={{ OverlayLoading: () => <div /> }}
        actions={[
          (data) => ({
            icon: QueryStatsIcon,
            tooltip: 'View Audits',
            onClick: (event, rowData) => {
              handleAuditOpen(rowData);
            },
            disabled: false
          }),
          (data) => ({
            icon: () => <EditIcon />,
            tooltip: 'Edit',
            onClick: (event, rowData) => {
              handleCreationOpen(rowData);
            },
            disabled: data.isDefault
          }),
          {
            icon: () => <RefreshIcon />,
            tooltip: 'Refresh Data',
            onClick: () => {
              refresh();
            },
            isFreeAction: true
          },
          {
            icon: () => <AddIcon />,
            onClick: () => {
              handleCreationOpen({});
            },
            isFreeAction: true
          }
        ]}
        icons={{
          ...materialTableIcons
        }}
        onRowClick={(event, rowData, togglePanel) => {
          if (!event.target.closest?.('button, input, [data-no-row-toggle="true"]')) {
            togglePanel();
          }
        }}
        detailPanel={({ rowData }) => (
          <Box m={2} data-no-row-toggle="true">
            <Grid container spacing={0}>
              {FILTER_OPTION_SETTINGS.map(({ title, field }) => (
                <Grid item xs={12} key={field}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={rowData[field] !== false}
                        onChange={(_, checked) => handleFilterOptionCheckboxChange(rowData, field, checked, title)}
                        inputProps={{ 'aria-label': `${title.toLowerCase()} checkbox` }}
                        id={`input-${field}-company-${rowData.id}`}
                        color="primary"
                      />
                    }
                    label={title}
                    labelPlacement="end"
                    sx={{ m: 0, minHeight: 28 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      />
      <TenantCreationDialog open={creationOpen} target={tenantData.current} onClose={handleCreationClose} />
      <Snackbar open={!!errorMessage} autoHideDuration={6000} onClose={() => setErrorMessage('')}>
        <Alert onClose={() => setErrorMessage('')} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
      <Dialog open={auditOpen} onClose={handleAuditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Audit Log {auditTarget ? `for ${auditTarget.name}` : ''}</DialogTitle>
        <DialogContent dividers>
          {auditLoading ? (
            <CircularProgress />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Update Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  auditRows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.action || '-'}</TableCell>
                      <TableCell>{row.username || row.updatedBy || '-'}</TableCell>
                      <TableCell>{moment(row.timestamp).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAuditClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TenantManagement;
